/**
 * @typedef {import('./Props').default} Props
 * @typedef {import('./State').default} State
 */

import { translate } from '../../../Internationalization';
import { withHomeownerService } from '../../../../service-container';
import { withProperty } from '../../PropertyProvider';
import captureError from '../../../../utils/captureError';
import images from '../../../../config/local/images';
import LinkButton from '../../../../shared/LinkButton';
import Loading from '../../../../shared/Loading';
import paths from '../../../../config/local/paths';
import PropertyImage from './PropertyImage/PropertyImage';
import PropertyInfo from './PropertyInfo/PropertyInfo';
import React from 'react';

/** @extends {React.Component<Props, State>} */
class PropertyCard extends React.Component {
	/** @type {string | null} */
	#activePropertyRequestId = null;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State}  */
		this.state = {
			errorMessage: null,
			image: null,
			isGettingData: true,
			propertyDetails: null,
			propertyId: null,
		};
	}

	async componentDidMount() {
		await this.#setPropertyDetails();
	}

	/**
	 * @param {Props} prevProps
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps) {
		const propertyId = this.#getPropertyId();
		const previousPropertyId = PropertyCard.getPropertyId(prevProps);

		if (propertyId !== previousPropertyId) {
			await this.#setPropertyDetails();
		}
	}

	/**
	 * @param {Props} props
	 * @returns {string | undefined}
	 */
	static getPropertyId(props) {
		const propertyList = props.property.propertyList ?? [];
		const dashboardProperty =
			propertyList.find((property) => property.isPrimary) ??
			propertyList[0];

		return (
			dashboardProperty?.id ??
			props.property.selectedProperty?.id ??
			undefined
		);
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return PropertyCard.getPropertyId(this.props);
	}

	/** @returns {Promise<void>} */
	async #setPropertyDetails() {
		const propertyId = this.#getPropertyId();

		if (!propertyId) {
			this.setState({
				errorMessage: translate(
					'homeowner.pages.dashboard.properties.property_info_error'
				),
				isGettingData: false,
			});
			return;
		}

		this.#activePropertyRequestId = propertyId;
		this.setState({
			errorMessage: null,
			isGettingData: true,
		});

		try {
			const propertyInfo =
				await this.props.homeownerService.getHomeownerPrimaryProperty(
					propertyId
				);

			if (this.#activePropertyRequestId !== propertyId) {
				return;
			}

			this.setState({
				image: propertyInfo.image,
				isGettingData: false,
				propertyDetails: propertyInfo.details,
				propertyId: propertyInfo.id,
			});
		} catch (error) {
			captureError(error);

			if (this.#activePropertyRequestId !== propertyId) {
				return;
			}

			this.setState({
				errorMessage: translate(
					'homeowner.pages.dashboard.properties.property_info_error'
				),
				isGettingData: false,
				propertyDetails: null,
			});
		}
	}

	render() {
		if (this.state.errorMessage) {
			return <p>{this.state.errorMessage}</p>;
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		/**
		 * @type {NonNullable<State['propertyDetails']>}
		 */
		// @ts-ignore the only time property details  is null is when errorMessage is truthy*/
		const details = this.state.propertyDetails;
		const propertyId = this.state.propertyId ?? this.#getPropertyId();
		const propertyDetailsPath = propertyId
			? paths.app.homeowner.properties.scoped.details.replace(
					':propertyId',
					propertyId
			  )
			: paths.app.homeowner.properties.root;

		return (
			<section className="card card-property-detail horizontal card-border mt-3">
				<PropertyImage image={this.state.image} />

				<section className="card-body property-details">
					<PropertyInfo propertyDetails={details} />
					<div className="py-2">
						<LinkButton
							className="button button-icon mx-auto"
							icon={images.icons.properties}
							iconClass="icon icon-fill-fg icon-sm"
							to={{
								pathname: propertyDetailsPath,
								state: {
									referrer: `${location.pathname}${location.search}${location.hash}`,
								},
							}}
						>
							{translate(
								'homeowner.pages.dashboard.properties.cta'
							)}
						</LinkButton>
					</div>
				</section>
			</section>
		);
	}
}

// @ts-ignore
export default withHomeownerService(withProperty(PropertyCard));
